import { capitalize } from "../utils/text";
import { apartmentTypeToLabel } from "../utils/apartmentType";
import { format } from "../utils/dateTime";
import { integerFormat, decimalFormat } from "./currency";

const getStatus = (data) => {
  if (
    data.construction &&
    data.construction.ready &&
    data.availability.toLowerCase() === "myynnissä"
  ) {
    return "Muuttovalmis";
  } else {
    return data.availability;
  }
};

const getStatusText = (data) => {
  return capitalize(
    getStatus(data) +
      (data.status.current.toLowerCase() === "varattu"
        ? ""
        : " ja varattavissa")
  );
};

const getSiteType = (data) => {
  switch (data.siteType) {
    case "OWN":
      return "Oma tontti";
    case "RENT":
      return "Vuokratontti";
    case "RENT_OPT":
      return "Valinnainen vuokra";
    default:
      return null;
  }
};

const getDetails = (data) => {
  let details = [
    {
      label: "Huoneistotyyppi",
      value: data.features.roomTypes,
    },
    {
      label: "Pinta-ala",
      value: data.sizeM2 + " m2",
    },
    {
      label: "Myyntihinta",
      value: integerFormat.format(data.salesPrice),
    },
    {
      label: "Velaton hinta",
      value: integerFormat.format(data.salesPriceUnencumbered),
    },
    {
      label: "Kerros",
      value: data.floorLocation,
    },
    {
      label: "Talotyyppi",
      value: apartmentTypeToLabel(data.type),
    },
  ];

  if (data.construction && !data.construction.ready) {
    details = [
      ...details,
      {
        label: "Arvioitu valmistumisaika",
        value: data.construction.estimatedCompletion,
      },
    ];
  }
  return details;
};

const getExtraDetails = (data) => {
  const details = [
    {
      label: "Osoite",
      value: data.location.address,
    },
    {
      label: "Hoitovastike",
      value: decimalFormat.format(data.maintenanceFee) + "/kk",
    },
    {
      label: "Rahoitusvastike lyhennysvapaan ajan",
      value: decimalFormat.format(data.financingFee) + "/kk",
    },
    {
      label: "Rahoitusvastike lyhennysvapaan jälkeen",
      value:
        decimalFormat.format(data.financingFeeIncludingInstallment) + "/kk",
    },
  ];
  let siteType = getSiteType(data);
  if (siteType) {
    details.push({
      label: "Tontti",
      value: siteType,
    });
  }
  if (data.siteCondomiumFee) {
    details.push({
      label: "Tontin vuokravastike",
      value: decimalFormat.format(data.siteCondomiumFee),
    });
  }
  if (data.siteRepurchacePrice) {
    details.push({
      label: "Tontin lunastushinta",
      value: integerFormat.format(data.siteRepurchacePrice),
    });
  }
  return details;
};

const getShowingTime = (data) => {
  if (!data.showingTime || !data.showingTime.start) {
    return null;
  }

  const showingTimeDate = new Date(data.showingTime.start);
  if (showingTimeDate.getTime() > Date.now()) {
    const formattedStart = format(showingTimeDate);
    const formattedEnd = format(new Date(data.showingTime.end));
    return {
      start: `${formattedStart.day}.${formattedStart.month} klo ${formattedStart.hour}:${formattedStart.minute}`,
      end: `${formattedEnd.hour}:${formattedEnd.minute}`,
    };
  }
  return null;
};

const getImages = (data) => {
  return [data.floorPlanImage, data.plotPlanImage, ...data.images];
};

export default (data) => {
  return {
    ...data,
    status: data.status.current,
    statusText: getStatusText(data),
    available: data.status.current.toLowerCase() !== "varattu",
    title: data.location.number + ", " + data.housingCompanyName,
    titleLocation: data.location.region
      ? data.location.region + ", " + data.location.city
      : data.location.city,
    housingCompanyName: data.housingCompanyName,
    details: getDetails(data),
    extraDetails: getExtraDetails(data),
    showingTime: getShowingTime(data),
    images: getImages(data),
    size: data.sizeM2,
    price: data.salesPrice,
    priceUnencumbered: data.salesPriceUnencumbered,
  };
};
